import { inject } from 'inversify';
import { APP_TIMEZONE } from '../../config/timezone';
import { Injectable } from '../../decorators/injectable';
import { HttpService, type IHttpService } from '../http/http.service';
import type {
  AddBatchItemDto,
  AddItemDto,
  CompleteReceiptDto,
  CreateDeliveryNoteDto,
  CreateSupplierDto,
  DeliveryItem,
  DeliveryNote,
  DeliveryNoteApiResponse,
  DeliveryNotePhoto,
  DeliveryNotesPage,
  DuplicateCheckResponse,
  IGoodsReceivingExtractionService,
  IGoodsReceivingService,
  LabelPhoto,
  RecallCheckResponse,
  Supplier,
  SupplierApiResponse,
  UpdateDraftDto,
  UpdateItemDto,
  UpdateItemStatusDto,
  ValidateItemDto,
} from './goods-receiving.types';
import { GoodsReceivingExtractionService } from './goods-receiving-extraction.service';

interface ItemApiResponse {
  id: string;
  productName: string;
  batchNumber: string | null;
  expiryDate: string | null;
  hasNoExpiry?: boolean;
  quantity: number;
  unit: string;
  status: string;
  statusReason: string | null;
  isCompliant: boolean;
  labelPhotos: Array<{
    id: string;
    storageKey: string;
    originalFilename: string;
  }>;
  allergens?: Array<{
    allergenId: string;
    allergenCode: string;
    allergenName: string;
    category: 'CONTAINS' | 'MAY_CONTAIN';
  }>;
  pastPurchaseCount?: number;
  lastPurchaseDate?: string | null;
}

function mapItemApiToDeliveryItem(item: ItemApiResponse): DeliveryItem {
  return {
    id: item.id,
    productName: item.productName,
    batchNumber: item.batchNumber,
    expiryDate: item.expiryDate,
    hasNoExpiry: item.hasNoExpiry ?? false,
    quantity: item.quantity,
    unit: item.unit,
    status: item.status as DeliveryItem['status'],
    statusReason: item.statusReason,
    isCompliant: item.isCompliant,
    labelPhotos: (item.labelPhotos ?? []).map((photo) => ({
      id: photo.id,
      storageKey: photo.storageKey,
      originalFilename: photo.originalFilename,
    })),
    allergens: (item.allergens ?? []).map((a) => ({
      allergenId: a.allergenId,
      allergenCode: a.allergenCode,
      allergenName: a.allergenName,
      category: a.category,
    })),
    pastPurchaseCount: item.pastPurchaseCount,
    lastPurchaseDate: item.lastPurchaseDate,
  };
}

@Injectable()
class GoodsReceivingService implements IGoodsReceivingService {
  constructor(
    @inject(HttpService) private readonly httpService: IHttpService,
    @inject(GoodsReceivingExtractionService)
    private readonly extractionService: IGoodsReceivingExtractionService,
  ) {}

  private mapDeliveryNoteApiToDeliveryNote(api: DeliveryNoteApiResponse): DeliveryNote {
    return {
      id: api.id,
      documentNumber: api.documentNumber,
      date: api.date,
      receivedAt: api.receivedAt,
      status: api.status as 'DRAFT' | 'COMPLETED' | 'ARCHIVED',
      temperature: api.temperature,
      vehicleHygiene: api.vehicleHygiene,
      packagingIntact: api.packagingIntact,
      notes: api.notes,
      supplier: api.supplier
        ? {
            id: api.supplier.id,
            name: api.supplier.name,
            vatNumber: api.supplier.vatNumber,
            address: api.supplier.address ?? undefined,
            email: api.supplier.email ?? undefined,
            phone: api.supplier.phone ?? undefined,
          }
        : null,
      items: api.items.map((item) => ({
        id: item.id,
        productName: item.productName,
        batchNumber: item.batchNumber,
        expiryDate: item.expiryDate,
        hasNoExpiry: item.hasNoExpiry,
        quantity: item.quantity,
        unit: item.unit,
        unitPrice: item.unitPrice ?? null,
        lineTotal: item.lineTotal ?? null,
        currency: item.currency ?? null,
        status: item.status as 'PENDING' | 'ACCEPTED' | 'ACCEPTED_WITH_RESERVATION' | 'REJECTED',
        statusReason: item.statusReason,
        isCompliant: item.isCompliant,
        pastPurchaseCount: item.pastPurchaseCount,
        lastPurchaseDate: item.lastPurchaseDate,
        labelPhotos: (item.labelPhotos ?? []).map((photo) => ({
          id: photo.id,
          storageKey: photo.storageKey,
          originalFilename: photo.originalFilename,
        })),
        allergens: (item.allergens ?? []).map(
          (a: {
            allergenId: string;
            allergenCode: string;
            allergenName: string;
            category: 'CONTAINS' | 'MAY_CONTAIN';
          }) => ({
            allergenId: a.allergenId,
            allergenCode: a.allergenCode,
            allergenName: a.allergenName,
            category: a.category,
          }),
        ),
      })),
      photos:
        api.photos?.map((photo) => ({
          id: photo.id,
          storageKey: photo.storageKey,
          originalFilename: photo.originalFilename,
          sourceKey: photo.sourceKey ?? undefined,
        })) ?? [],
      lots: api.lots,
      currentStep: api.currentStep ?? undefined,
      itemsCount: api.itemsCount ?? 0,
      allItemsValidated: api.allItemsValidated ?? false,
      extractionStatus: (api.extractionStatus as DeliveryNote['extractionStatus']) ?? 'IDLE',
      extractionResult: (api.extractionResult as DeliveryNote['extractionResult']) ?? null,
    };
  }

  private mapSupplierApiToSupplier(api: SupplierApiResponse): Supplier {
    return {
      id: api.id,
      name: api.name,
      vatNumber: api.vatNumber,
      address: api.address ?? undefined,
      email: api.email ?? undefined,
      phone: api.phone ?? undefined,
    };
  }

  // Fornitori
  async getSuppliers(): Promise<Supplier[]> {
    const suppliers = await this.httpService.get<SupplierApiResponse[]>('delivery/suppliers');
    return suppliers.map((s) => this.mapSupplierApiToSupplier(s));
  }

  async createSupplier(data: CreateSupplierDto): Promise<Supplier> {
    const supplier = await this.httpService.post<SupplierApiResponse>('delivery/suppliers', data);
    return this.mapSupplierApiToSupplier(supplier);
  }

  // Prodotti
  async getProductNames(): Promise<string[]> {
    return this.httpService.get<string[]>('delivery/products');
  }

  // DDT
  async findAll(from?: string, to?: string, page = 1): Promise<DeliveryNotesPage> {
    const params = new URLSearchParams();
    if (from) {
      params.set('from', from);
    }
    if (to) {
      params.set('to', to);
    }
    params.set('tz', APP_TIMEZONE);
    if (page > 1) {
      params.set('page', String(page));
    }

    const queryString = params.toString();
    const url = queryString ? `delivery/delivery-notes?${queryString}` : 'delivery/delivery-notes';

    const response = await this.httpService.get<{
      items: DeliveryNoteApiResponse[];
      total: number;
      page: number;
      pageSize: number;
    }>(url);
    return {
      ...response,
      items: response.items.map((note) => this.mapDeliveryNoteApiToDeliveryNote(note)),
    };
  }

  async findById(id: string, options?: { includeLots?: boolean }): Promise<DeliveryNote | null> {
    try {
      const params = options?.includeLots ? '?includeLots=true' : '';
      const note = await this.httpService.get<DeliveryNoteApiResponse>(
        `delivery/delivery-notes/${id}${params}`,
      );
      return this.mapDeliveryNoteApiToDeliveryNote(note);
    } catch {
      return null;
    }
  }

  async createDeliveryNote(data: CreateDeliveryNoteDto, files: File[]): Promise<DeliveryNote> {
    const formData = new FormData();
    formData.append('supplierId', data.supplierId);
    formData.append('documentNumber', data.documentNumber);
    formData.append('date', data.date);
    if (data.temperature !== undefined && data.temperature !== null) {
      formData.append('temperature', data.temperature.toString());
    }
    if (data.vehicleHygiene !== undefined) {
      formData.append('vehicleHygiene', data.vehicleHygiene.toString());
    }
    if (data.packagingIntact !== undefined) {
      formData.append('packagingIntact', data.packagingIntact.toString());
    }
    if (data.notes) {
      formData.append('notes', data.notes);
    }

    for (const file of files) {
      formData.append('files', file);
    }

    const note = await this.httpService.postForm<DeliveryNoteApiResponse>(
      'delivery/delivery-notes',
      formData,
    );
    return this.mapDeliveryNoteApiToDeliveryNote(note);
  }

  async closeDeliveryNote(id: string): Promise<DeliveryNote> {
    const note = await this.httpService.post<DeliveryNoteApiResponse>(
      `delivery/delivery-notes/${id}/commit`,
    );
    return this.mapDeliveryNoteApiToDeliveryNote(note);
  }

  // Bozze
  async findDrafts(): Promise<DeliveryNote[]> {
    const notes = await this.httpService.get<DeliveryNoteApiResponse[]>(
      'delivery/delivery-notes?status=DRAFT',
    );
    return notes.map((note) => this.mapDeliveryNoteApiToDeliveryNote(note));
  }

  async createDraft(): Promise<DeliveryNote> {
    const note = await this.httpService.post<DeliveryNoteApiResponse>(
      'delivery/delivery-notes/draft',
    );
    return this.mapDeliveryNoteApiToDeliveryNote(note);
  }

  async updateDraft(id: string, data: UpdateDraftDto): Promise<DeliveryNote> {
    const note = await this.httpService.patch<DeliveryNoteApiResponse>(
      `delivery/delivery-notes/${id}`,
      data,
    );
    return this.mapDeliveryNoteApiToDeliveryNote(note);
  }

  async deleteDraft(id: string): Promise<void> {
    await this.httpService.delete(`delivery/delivery-notes/${id}`);
  }

  // Foto DDT
  async addPhotos(
    deliveryNoteId: string,
    files: File[],
    options?: { extract?: boolean; appendPages?: boolean },
  ): Promise<DeliveryNotePhoto[]> {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    // appendPages only makes sense together with extract: the server keeps the stored photos.
    const extractParam = options?.extract
      ? `?extract=true${options.appendPages ? '&appendPages=true' : ''}`
      : '';
    return this.httpService.postForm<DeliveryNotePhoto[]>(
      `delivery/delivery-notes/${deliveryNoteId}/photos${extractParam}`,
      formData,
    );
  }

  async deletePhoto(deliveryNoteId: string, photoId: string): Promise<void> {
    await this.httpService.delete(`delivery/delivery-notes/${deliveryNoteId}/photos/${photoId}`);
  }

  async checkDuplicate(deliveryNoteId: string): Promise<DuplicateCheckResponse> {
    return this.httpService.get<DuplicateCheckResponse>(
      `delivery/delivery-notes/${deliveryNoteId}/duplicate-check`,
    );
  }

  async checkRecalls(deliveryNoteId: string): Promise<RecallCheckResponse> {
    return this.httpService.get<RecallCheckResponse>(
      `delivery/delivery-notes/${deliveryNoteId}/recall-check`,
    );
  }

  async completeReceipt(
    data: CompleteReceiptDto,
    documentPhotos: File[],
    labelPhotosByItem: Map<number, File[]>,
  ): Promise<DeliveryNote> {
    const formData = new FormData();
    formData.append('data', JSON.stringify(data));

    for (const photo of documentPhotos) {
      formData.append('files', photo);
    }

    for (const [itemIndex, photos] of labelPhotosByItem.entries()) {
      for (let photoIndex = 0; photoIndex < photos.length; photoIndex++) {
        const photo = photos[photoIndex];
        const renamedFile = new File(
          [photo],
          `labelPhoto_${itemIndex}_${photoIndex}_${photo.name}`,
          { type: photo.type },
        );
        formData.append('files', renamedFile);
      }
    }

    const note = await this.httpService.postForm<DeliveryNoteApiResponse>(
      'delivery/complete-receipt',
      formData,
    );
    return this.mapDeliveryNoteApiToDeliveryNote(note);
  }

  // Estrazione AI - delegated to extraction service (solo per etichette singoli articoli)
  async extractItemData(file: File): Promise<{
    productName: string | null;
    quantity: string | null;
    unit: string | null;
    batchNumber: string | null;
    expiryDate: string | null;
    allergens: Array<{ allergenCode: string; category: 'CONTAINS' | 'MAY_CONTAIN' }> | null;
  } | null> {
    return this.extractionService.extractItemData(file);
  }

  // Articoli
  async addItem(deliveryNoteId: string, item: AddItemDto, files?: File[]): Promise<DeliveryItem> {
    const formData = new FormData();
    formData.append('productName', item.productName);
    formData.append('quantity', item.quantity.toString());
    formData.append('unit', item.unit);
    formData.append('batchNumber', item.batchNumber);
    formData.append('expiryDate', item.expiryDate);
    if (item.hasNoExpiry !== undefined) {
      formData.append('hasNoExpiry', String(item.hasNoExpiry));
    }
    if (item.isDamaged !== undefined) {
      formData.append('isDamaged', item.isDamaged.toString());
    }
    if (item.isTemperatureNonCompliant !== undefined) {
      formData.append('isTemperatureNonCompliant', item.isTemperatureNonCompliant.toString());
    }
    if (item.isHygieneNonCompliant !== undefined) {
      formData.append('isHygieneNonCompliant', item.isHygieneNonCompliant.toString());
    }
    if (item.allergens && item.allergens.length > 0) {
      formData.append('allergens', JSON.stringify(item.allergens));
    }
    if (item.unitPrice != null) {
      formData.append('unitPrice', String(item.unitPrice));
    }
    if (item.lineTotal != null) {
      formData.append('lineTotal', String(item.lineTotal));
    }
    if (item.currency) {
      formData.append('currency', item.currency);
    }

    if (files && files.length > 0) {
      for (const file of files) {
        formData.append('files', file);
      }
    }

    const response = await this.httpService.postForm<ItemApiResponse>(
      `delivery/delivery-notes/${deliveryNoteId}/items`,
      formData,
    );

    return mapItemApiToDeliveryItem(response);
  }

  async addBatchItems(deliveryNoteId: string, items: AddBatchItemDto[]): Promise<DeliveryItem[]> {
    const response = await this.httpService.post<ItemApiResponse[]>(
      `delivery/delivery-notes/${deliveryNoteId}/items/batch`,
      { items },
    );

    return response.map(mapItemApiToDeliveryItem);
  }

  async deleteItem(itemId: string): Promise<void> {
    await this.httpService.delete(`delivery/items/${itemId}`);
  }

  async updateItem(itemId: string, data: UpdateItemDto): Promise<DeliveryItem> {
    const response = await this.httpService.patch<ItemApiResponse>(
      `delivery/items/${itemId}`,
      data,
    );
    return mapItemApiToDeliveryItem(response);
  }

  async validateItem(itemId: string, data: ValidateItemDto): Promise<DeliveryItem> {
    const response = await this.httpService.patch<ItemApiResponse>(
      `delivery/items/${itemId}/validate`,
      {
        batchNumber: data.batchNumber,
        expiryDate: data.expiryDate,
        hasNoExpiry: data.hasNoExpiry,
      },
    );
    return mapItemApiToDeliveryItem(response);
  }

  async updateItemStatus(itemId: string, data: UpdateItemStatusDto): Promise<DeliveryItem> {
    const response = await this.httpService.patch<ItemApiResponse>(
      `delivery/items/${itemId}/status`,
      data,
    );
    return mapItemApiToDeliveryItem(response);
  }

  // Foto
  async uploadLabelPhoto(itemId: string, file: File): Promise<LabelPhoto> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.httpService.postForm<{
      id: string;
      storageKey: string;
      originalFilename: string;
    }>(`delivery/items/${itemId}/label-photos`, formData);

    return {
      id: response.id,
      storageKey: response.storageKey,
      originalFilename: response.originalFilename,
    };
  }

  // Storage
  async getPresignedUrl(storageKey: string): Promise<string> {
    const response = await this.httpService.get<{
      url: string;
      expiresIn: number;
    }>(`storage/${encodeURIComponent(storageKey)}/presign`);
    return response.url;
  }
}

export type { IGoodsReceivingService } from './goods-receiving.types';
export { GoodsReceivingService };
